import React, { useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { RiDeleteBinLine } from "react-icons/ri";
import apiEndpoints from "../../apiconfig";
import axios from "axios";
import { toast } from "react-toastify";

const EditGroupModal = ({ group, onClose, refreshGroups }) => {
    const [contacts, setContacts] = useState(group.contacts || []);
    const [newContact, setNewContact] = useState({ name: "", mobile: "" });
    const [isLoading, setIsLoading] = useState(false);

    // Get token from localStorage
    const getToken = () => {
        return localStorage.getItem('token');
    };

    const handleDeleteContact = async (id) => {
        try {
            const token = getToken();
            await axios.post(apiEndpoints.addContact, {
                action: "delete_contact_from_group",
                group_name: group.name,
                contact_id: id,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            toast.success("Contact removed from group");
            setContacts((prev) => prev.filter((c) => c.id !== id));
            refreshGroups();
        } catch (err) {
            console.error("Error deleting contact:", err);
            if (err.response?.status === 401) {
                toast.error("Session expired. Please login again.");
            } else {
                toast.error("Failed to remove contact");
            }
        }
    };

    const handleAddContact = async () => {
        if (!newContact.name || !newContact.mobile)
            return toast.error("Name and mobile number required");

        try {
            setIsLoading(true);
            const token = getToken();
            const response = await axios.post(apiEndpoints.addContact, {
                action: "add_contact_to_group",
                group_name: group.name,
                contact_name: newContact.name,
                mobile_number: newContact.mobile,
                country_code: "+91", // Default country code
                tags: "", // Empty tags by default
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;
            if (result.status) {
                toast.success("Contact added successfully");
                // Add the new contact with the actual ID from the response
                setContacts((prev) => [
                    ...prev,
                    { 
                        id: result.contact_id || Date.now(), 
                        contact_name: newContact.name, 
                        mobile_number: newContact.mobile 
                    },
                ]);
                setNewContact({ name: "", mobile: "" });
                refreshGroups();
            } else {
                toast.error(result.message || "Failed to add contact");
            }
        } catch (err) {
            console.error("Error adding contact:", err);
            if (err.response?.status === 401) {
                toast.error("Session expired. Please login again.");
            } else if (err.response?.data?.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error("Failed to add contact");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddContact();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 font-['Montserrat']">
            <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl overflow-hidden animate-fadeIn">
                <div className="flex justify-between items-center px-6 py-4 bg-[#25D366] text-white">
                    <h2 className="text-lg font-semibold">Edit Group: {group.name}</h2>
                    <button 
                        onClick={onClose} 
                        className="p-1 hover:bg-white/20 rounded-full transition"
                    >
                        <AiOutlineClose size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                    {/* Add contact section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-gray-700 font-medium mb-3">Add New Contact to Group</h3>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                type="text"
                                placeholder="Contact Name"
                                value={newContact.name}
                                onChange={(e) =>
                                    setNewContact({ ...newContact, name: e.target.value })
                                }
                                onKeyPress={handleKeyPress}
                                className="border border-gray-300 rounded-md px-3 py-2 flex-1 focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none"
                            />
                            <input
                                type="text"
                                placeholder="Mobile Number (10 digits)"
                                value={newContact.mobile}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, ""); // remove non-digits
                                    if (value.length <= 10) {
                                        setNewContact({ ...newContact, mobile: value });
                                    }
                                }}
                                onKeyPress={handleKeyPress}
                                className="border border-gray-300 rounded-md px-3 py-2 flex-1 focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none"
                                maxLength={10}
                            />
                            <button
                                onClick={handleAddContact}
                                disabled={isLoading || !newContact.name || !newContact.mobile}
                                className="bg-[#25D366] text-white px-4 py-2 rounded-md hover:bg-[#1cb85c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isLoading ? "Adding..." : "Add Contact"}
                            </button>
                        </div>
                    </div>

                    {/* Contact list */}
                    <div>
                        <h3 className="text-gray-700 font-medium mb-3">Contacts in this group ({contacts.length}):</h3>
                        {contacts.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <p className="text-sm">No contacts in this group yet.</p>
                                <p className="text-xs mt-1">Add contacts using the form above.</p>
                            </div>
                        ) : (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <ul className="divide-y divide-gray-200">
                                    {contacts.map((c) => (
                                        <li
                                            key={c.id}
                                            className="flex justify-between items-center py-3 px-4 hover:bg-gray-50 transition-colors"
                                        >
                                            <div>
                                                <span className="font-medium text-gray-900">
                                                    {c.contact_name}
                                                </span>
                                                <span className="text-gray-600 ml-2">
                                                    ({c.mobile_number})
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => handleDeleteContact(c.id)}
                                                className="text-red-600 hover:text-red-700 p-1 rounded transition-colors"
                                                title="Remove from group"
                                            >
                                                <RiDeleteBinLine size={16} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 text-right border-t">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-sm font-medium transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditGroupModal;